import { type OutOfPocketLine, type HoursBySprint, type InsightSprint } from './insights';

/** Pure and importing only its own types: the org chart helpers drag the portfolio's data hooks in. */

/** The portfolio chart's own eight colours, so a line reads as the same kind of thing. */
export const LINE_COLORS = [1, 2, 3, 4, 5, 6, 7, 8].map(number => `var(--product-line-${number})`);

/** Hours land on halves and quarters, and adding them up in floating point does not. */
const round = (value: number): number => Math.round(value * 100) / 100;

/** As many lines as the plot stays legible with, which is what the picker caps a selection at. */
export const MAX_PEOPLE = 5;

/** A row of the grids as these cards need it: an id to add hours up by, and a name to put on them. */
export interface Counted {
  id: string;
  name: string;
}

export interface PersonLine {
  key: string;
  label: string;
  /** Hours in that sprint alone, aligned to the sprints given: the line as it is drawn. */
  perSprint: number[];
  /** The year's hours, all sprints added up. */
  total: number;
  color: string;
}

/** Busiest in the year first: the legend and the colours follow this order. */
export const peopleLines = (
  people: Counted[],
  sprints: Pick<InsightSprint, 'id'>[],
  hours: HoursBySprint,
): PersonLine[] =>
  people
    .map(person => {
      const perSprint = sprints.map(sprint => round(hours[sprint.id]?.[person.id] ?? 0));

      return {
        key: person.id,
        label: person.name,
        perSprint,
        total: perSprint.reduce((sum, amount) => round(sum + amount), 0),
      };
    })
    .filter(line => line.total > 0)
    .sort((one, two) => two.total - one.total)
    .map((line, index) => ({ ...line, color: LINE_COLORS[index % LINE_COLORS.length] }));

/** The colours wrap at eight, so a pick spanning the wrap would draw two people alike; recolour it. */
export const withDistinctColors = (lines: PersonLine[]): PersonLine[] => {
  const taken = new Set<string>();

  return lines.map(line => {
    const color = taken.has(line.color) ? (LINE_COLORS.find(one => !taken.has(one)) ?? line.color) : line.color;
    taken.add(color);

    return { ...line, color };
  });
};

/** Sprints counted as "now" when choosing who the card opens on. */
const RECENT_SPRINTS = 2;

const recentHours = (line: PersonLine): number =>
  line.perSprint.slice(-RECENT_SPRINTS).reduce((sum, hours) => sum + hours, 0);

/** The busiest few over the last two sprints, so someone who only worked on it early does not crowd out who is on it now. */
export const opensWith = (lines: PersonLine[]): string[] =>
  [...lines]
    .sort((one, two) => recentHours(two) - recentHours(one) || two.total - one.total)
    .slice(0, MAX_PEOPLE)
    .map(line => line.key);

/** The bought-in costs, dearest first: the card is asked which of them are the big ones. */
export const biggestFirst = (lines: OutOfPocketLine[]): OutOfPocketLine[] =>
  [...lines].sort((one, two) => two.total - one.total || one.name.localeCompare(two.name));

/** The next round number above the tallest line, so gridlines land on figures a reader knows. */
export const axisTop = (value: number): number => {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));

  return Math.ceil(value / magnitude) * magnitude;
};

/** Hours in the space a legend has: "160 h", and "1.2K h" once a year of them stops fitting. */
export const hoursShort = (hours: number): string =>
  // One decimal of a thousand: "1.24K h" is a figure nobody asked for in a slot this narrow.
  hours >= 1000 ? `${Math.round(hours / 100) / 10}K h` : `${round(hours)} h`;
